import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useCallback,
  useEffect,
} from "react";
import { TicketProcessingParams } from "../../models/TicketProcessingParams/ticketProcessing.model";
import { appRoutes } from "../../constants/appRoutes";
import { useLocation } from "react-router-dom";
import { TicketProcessingRecordType } from "../../Types/ticketProcessingTypes";

interface TicketProcessingFiltersContextType {
  processingFilters: TicketProcessingParams;
  updateProcessingFilters: (params: TicketProcessingParams) => void;
  resetProcessingFilters: () => void;
  selectedRows: TicketProcessingRecordType[];
  updateSelectedRows: (items: TicketProcessingRecordType[]) => void;
}

const TicketProcessingFilterContext = createContext<
  TicketProcessingFiltersContextType | undefined
>(undefined);

export const useTicketProcessingFilterContext =
  (): TicketProcessingFiltersContextType => {
    const context = useContext(TicketProcessingFilterContext);
    if (!context) {
      throw new Error(
        "useTruckDetailContext must be used within a truckDetailProvider",
      );
    }
    return context;
  };

export const TicketProcessingFiltersProvider: React.FC<{
  children: ReactNode;
}> = ({ children }) => {
  const location = useLocation();
  const [processingFilters, setProcessingFilters] =
    useState<TicketProcessingParams>({
      customerName: undefined,
      jobName: undefined,
      truckNumber: undefined,
      startDate: undefined,
      endDate: undefined,
    });

  const [selectedRows, setSelectedRows] = useState([]);

  const updateProcessingFilters = useCallback((params) => {
    setProcessingFilters((prevState) => {
      const updatedFilters = params ? { ...prevState, ...params } : prevState;
      return { ...updatedFilters };
    });
  }, []);

  const updateSelectedRows = useCallback((newList) => {
    setSelectedRows(newList);
  }, []);

  const resetProcessingFilters = () => {
    setProcessingFilters({
      customerName: undefined,
      jobName: undefined,
      truckNumber: undefined,
      startDate: undefined,
      endDate: undefined,
    });
  };

  useEffect(() => {
    if(location && !location?.pathname?.startsWith(appRoutes.VIEW_TICKET_PROCESSING_LISTING)) {
      resetProcessingFilters();
      updateSelectedRows([]);
    }
  }, [location?.pathname])

  return (
    <TicketProcessingFilterContext.Provider
      value={{
        processingFilters,
        updateProcessingFilters,
        resetProcessingFilters,
        selectedRows,
        updateSelectedRows,
      }}
    >
      {children}
    </TicketProcessingFilterContext.Provider>
  );
};
